import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import { getSettings, updateSettings } from '../../services/SettingsService';
import {
    PREDATORS,
    hasPredatorConfig,
    huntBlockedReason,
    isHuntPaused,
    startPayload,
    startedText
} from './predators';
import './Predadores.css';

function apiError(err) {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    return data?.message || err?.message || 'Não foi possível ligar o trade.';
}

function statusBadge(settings, id) {
    if (!settings || settings.predadorHuntMode !== id) return null;
    if (isHuntPaused(settings)) return { text: 'Pausado', className: 'predators-badge paused' };
    return { text: 'Ligado', className: 'predators-badge' };
}

function PredatorCard({ predator, settings, loading, busyId, notice, onStart }) {
    const badge = statusBadge(settings, predator.id);
    const starting = busyId === predator.id;
    return (
        <article className={`predators-card p${predator.index.replace(/^0/, '')}`}>
            <div className="predators-card-top">
                <div>
                    <span className="predators-tag">{predator.tag}</span>
                    <h2>{predator.name}</h2>
                </div>
                <span className="predators-index">{predator.index}</span>
            </div>
            <p>{predator.summary}</p>
            {badge ? <span className={badge.className}>{badge.text}</span> : null}
            {notice?.error ? <p className="predators-error">{notice.error}</p> : null}
            {notice?.text ? (
                <div className={`predators-result${notice.warning ? ' warn' : ''}`}>
                    <strong>{notice.text}</strong>
                    {notice.warning ? <span className="predators-note">{notice.warning}</span> : null}
                </div>
            ) : null}
            <button
                type="button"
                className="predators-start"
                onClick={() => onStart(predator)}
                disabled={starting || loading || !settings || !!busyId}
            >
                {starting ? 'Ligando...' : 'Começar'}
            </button>
        </article>
    );
}

function PredatorGrid() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [busyId, setBusyId] = useState('');
    const [notice, setNotice] = useState(null);

    useEffect(() => {
        let alive = true;
        getSettings()
            .then(result => {
                if (alive) setSettings(result || {});
            })
            .catch(err => {
                if (alive) setLoadError(apiError(err));
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => { alive = false; };
    }, []);

    async function onStart(predator) {
        if (busyId || loading || !settings) return;
        setBusyId(predator.id);
        setNotice({ id: predator.id });
        const usedDefault = !hasPredatorConfig(settings);
        try {
            const saved = await updateSettings(startPayload(predator.id, settings));
            const next = saved && typeof saved === 'object' ? { ...settings, ...saved } : settings;
            setSettings(next);
            setNotice({
                id: predator.id,
                text: startedText(predator, usedDefault ? next : settings, usedDefault),
                warning: huntBlockedReason(next)
            });
        } catch (err) {
            setNotice({ id: predator.id, error: apiError(err) });
        } finally {
            setBusyId('');
        }
    }

    return (
        <>
            <header className="predators-head">
                <h1>Opções de trade</h1>
                <p>Quatro jeitos de fazer lucro. Nas três primeiras, o trade acompanha a alta da moeda e vende na reversão. O piso é 1,2%. O topo é livre: pode ser 3%, 5% ou 7%.</p>
            </header>
            {loadError ? <p className="predators-error">{loadError}</p> : null}
            <div className="predators-grid">
                {PREDATORS.map(predator => (
                    <PredatorCard
                        key={predator.id}
                        predator={predator}
                        settings={settings}
                        loading={loading}
                        busyId={busyId}
                        notice={notice?.id === predator.id ? notice : null}
                        onStart={onStart}
                    />
                ))}
            </div>
        </>
    );
}

function Predadores() {
    const { mode } = useParams();
    if (mode) return <Navigate to="/predadores" replace />;

    return (
        <>
            <Menu />
            <main className="content">
                <div className="predators-page">
                    <PredatorGrid />
                </div>
            </main>
            <Footer />
        </>
    );
}

export default Predadores;
